import dayjs from 'dayjs'
import { BaseChartData, ChartDayData, TimeFrame } from 'types/analytics'
import { formatUTCDay } from 'utils/analytics/date'

export type ChartSeriesPoint = BaseChartData & { t: number; label: string; index: number }

export type SeriesByTimeframe = Record<TimeFrame, ChartSeriesPoint[]>

export interface AnalyticsChartSeries {
  raw: ChartDayData[]
  latestEntry?: ChartDayData
  tvl: SeriesByTimeframe
  volume: SeriesByTimeframe
  fees: SeriesByTimeframe
}

const buildTvlSeriesByTimeframe = (daily: ChartSeriesPoint[]): SeriesByTimeframe => {
  const aggregate = (unit: 'week' | 'month') => {
    if (!daily.length) return [] as ChartSeriesPoint[]
    const aggregated: ChartSeriesPoint[] = []
    let currentKey: number | undefined
    let currentPoint: ChartSeriesPoint | undefined

    daily.forEach((point) => {
      const key = dayjs(point.t).startOf(unit).valueOf()
      if (currentKey === undefined) {
        currentKey = key
        currentPoint = point
        return
      }

      if (key !== currentKey) {
        if (currentPoint) aggregated.push(currentPoint)
        currentKey = key
      }

      currentPoint = point
    })

    if (currentPoint) {
      aggregated.push(currentPoint)
    }

    return aggregated
  }

  return {
    D: daily,
    W: aggregate('week'),
    M: aggregate('month'),
  }
}

const buildSumSeriesByTimeframe = (daily: ChartSeriesPoint[]): SeriesByTimeframe => {
  const aggregate = (unit: 'week' | 'month') => {
    if (!daily.length) return [] as ChartSeriesPoint[]
    const buckets = new Map<number, ChartSeriesPoint>()

    daily.forEach((point) => {
      const bucketKey = dayjs(point.t).startOf(unit).valueOf()
      const existing = buckets.get(bucketKey)

      if (existing) {
        existing.value += point.value
        if (point.t > existing.t) {
          existing.time = point.time
          existing.t = point.t
          existing.label = point.label
          existing.index = point.index
        }
      } else {
        buckets.set(bucketKey, { ...point })
      }
    })

    return Array.from(buckets.entries())
      .sort((a, b) => a[0] - b[0])
      .map(([, value]) => value)
  }

  return {
    D: daily,
    W: aggregate('week'),
    M: aggregate('month'),
  }
}

export const buildAnalyticsChartSeries = (raw: ChartDayData[] | undefined): AnalyticsChartSeries => {
  if (!raw || raw.length === 0) {
    return {
      raw: [],
      latestEntry: undefined,
      tvl: { D: [], W: [], M: [] },
      volume: { D: [], W: [], M: [] },
      fees: { D: [], W: [], M: [] },
    }
  }

  const chronological = [...raw].reverse()

  const tvlDaily: ChartSeriesPoint[] = []
  const volumeDaily: ChartSeriesPoint[] = []
  const feesDaily: ChartSeriesPoint[] = []

  chronological.forEach((entry, index) => {
    const timestamp = entry.date * 1000
    const time = formatUTCDay(entry.date)
    const label = dayjs(timestamp).format('MMM D, YYYY')
    const tvl = entry.tvlUSD ?? 0
    const volume = entry.volumeUSD ?? 0
    const fees = entry.feesUSD ?? 0

    tvlDaily.push({ time, value: tvl, t: timestamp, label, index })
    volumeDaily.push({ time, value: volume, t: timestamp, label, index })
    feesDaily.push({ time, value: fees, t: timestamp, label, index })
  })

  const tvlSeries = buildTvlSeriesByTimeframe(tvlDaily)
  const volumeSeries = buildSumSeriesByTimeframe(volumeDaily)
  const feesSeries = buildSumSeriesByTimeframe(feesDaily)

  return {
    raw,
    latestEntry: raw[0],
    tvl: tvlSeries,
    volume: volumeSeries,
    fees: feesSeries,
  }
}

export const downsampleSeries = (series: ChartSeriesPoint[], maxPoints = 600): ChartSeriesPoint[] => {
  const length = series.length
  if (length <= maxPoints || maxPoints <= 0) return series

  const sampled: ChartSeriesPoint[] = []
  const step = Math.max(1, Math.ceil(length / maxPoints))

  for (let index = 0; index < length; index += step) {
    sampled.push(series[index])
  }

  const lastPoint = series[length - 1]
  if (sampled[sampled.length - 1] !== lastPoint) {
    sampled.push(lastPoint)
  }

  return sampled
}
